import React from "react";
import { cn } from "@/lib/utils";
import * as LucideIcons from "lucide-react";
import { Globe } from "lucide-react";

interface ServiceItem {
  id: string;
  label: string;
  icon: string;
  active?: boolean;
}

interface ServiceWorldMapProps {
  title?: string;
  subtitle?: string;
  services: ServiceItem[];
  animated?: boolean;
  className?: string;
}

export function ServiceWorldMap({
  title = "Unsere Servicewelt",
  subtitle,
  services,
  animated = true,
  className,
}: ServiceWorldMapProps) {
  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName];
    return Icon ? <Icon className="h-6 w-6" /> : <Globe className="h-6 w-6" />;
  };

  // Calculate positions around a circle
  const getPosition = (index: number, total: number) => {
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
    const radius = 38; // percentage from center
    const x = 50 + radius * Math.cos(angle);
    const y = 50 + radius * Math.sin(angle);
    return { x, y };
  };

  return (
    <div className={cn("relative w-full aspect-square max-w-md mx-auto", className)}>
      {/* Central Globe */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div className={cn(
          "w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/60",
          "flex items-center justify-center shadow-xl",
          animated && "animate-pulse"
        )}>
          <Globe className="h-12 w-12 text-primary-foreground" />
        </div>
        {title && (
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-center">
            <span className="font-semibold text-sm whitespace-nowrap">{title}</span>
          </div>
        )}
      </div>

      {/* Connection lines */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
        {services.map((_, index) => {
          const pos = getPosition(index, services.length);
          return (
            <line
              key={`line-${index}`}
              x1="50"
              y1="50"
              x2={pos.x}
              y2={pos.y}
              stroke="currentColor"
              strokeWidth="0.3"
              className="text-primary/30"
              strokeDasharray="2,2"
            />
          );
        })}
      </svg>

      {/* Service nodes */}
      {services.map((service, index) => {
        const pos = getPosition(index, services.length);
        return (
          <div
            key={service.id}
            className={cn(
              "absolute transform -translate-x-1/2 -translate-y-1/2",
              "flex flex-col items-center gap-2",
              animated && "animate-in fade-in zoom-in-50"
            )}
            style={{
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              animationDelay: animated ? `${index * 150}ms` : undefined,
            }}
          >
            <div className={cn(
              "w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110",
              service.active !== false
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground"
            )}>
              {getIcon(service.icon)}
            </div>
            <span className="text-xs font-medium text-center max-w-[80px] leading-tight">
              {service.label}
            </span>
          </div>
        );
      })}

      {/* Subtitle */}
      {subtitle && (
        <div className="absolute bottom-0 left-0 right-0 text-center">
          <p className="text-sm text-muted-foreground italic">{subtitle}</p>
        </div>
      )}
    </div>
  );
}
